import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Paper, Typography, Box, Skeleton, Tooltip } from '@mui/material';
import { WbSunny, WaterDrop, Air, Cloud, Grain, Thunderstorm, AcUnit, Warning } from '@mui/icons-material';
import axios from '../../config/axios';
import { API_ENDPOINTS } from '../../config/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { BRAND } from '../../theme/colors';

const getIcon = (desc = '', size = 32) => {
  const d = desc.toLowerCase();
  if (d.includes('cloud') || d.includes('overcast')) return <Cloud sx={{ fontSize: size, color: '#90a4ae' }} />;
  if (d.includes('rain') || d.includes('drizzle')) return <Grain sx={{ fontSize: size, color: '#42a5f5' }} />;
  if (d.includes('thunder') || d.includes('storm')) return <Thunderstorm sx={{ fontSize: size, color: '#7e57c2' }} />;
  if (d.includes('snow') || d.includes('sleet')) return <AcUnit sx={{ fontSize: size, color: '#4fc3f7' }} />;
  return <WbSunny sx={{ fontSize: size, color: '#ffa726' }} />;
};

const WeatherWidget = ({ compact = false }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [weather, setWeather] = useState(null);
  const [provider, setProvider] = useState(null);
  const [warning, setWarning] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(API_ENDPOINTS.WEATHER_CURRENT);
        setWeather(res.data.data);
        setProvider(res.data.provider || res.data.data?.provider || null);
        setWarning(res.data.warning || null);
      } catch {
        // Never fabricate weather. A made-up "24.5°C" in the top bar reads as
        // real to a client at the stand — the exact "show real data, not a
        // placeholder" bug. On failure show nothing and flag it unavailable.
        setWeather(null);
        setWarning(t('weatherUnavailable') || 'Weather unavailable');
      } finally {
        setLoading(false);
      }
    };
    fetch();
    const interval = setInterval(fetch, 600000);
    return () => clearInterval(interval);
  // A mount-only 10-minute poll; t only words the failure note, and re-running
  // on a language switch would refetch for nothing.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    if (compact) return <Skeleton width={60} height={24} />;
    return (
      <Paper sx={{ p: 1.5, width: 240 }}>
        <Skeleton width="60%" height={20} />
        <Skeleton width="40%" height={32} />
        <Skeleton width="80%" height={16} />
      </Paper>
    );
  }

  if (!weather) return null;

  // Compact: single-line inline display for top bars
  if (compact) {
    return (
      <Box
        onClick={() => navigate('/weather')}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.75,
          cursor: 'pointer', px: 1, py: 0.5,
          borderRadius: 1,
          border: warning ? '1px solid #ed6c02' : '1px solid #e0e0e0',
          '&:hover': { bgcolor: '#f5f5f5' },
          transition: '0.15s',
        }}
      >
        {getIcon(weather.description, 20)}
        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
          {Math.round(weather.temperature)}°C
        </Typography>
        <WaterDrop sx={{ fontSize: 14, color: '#1976d2', ml: 0.5 }} />
        <Typography sx={{ fontSize: '0.9rem' }}>{weather.humidity}%</Typography>
        {weather.precipitation > 0 && (
          <>
            <Grain sx={{ fontSize: 14, color: '#2196f3' }} />
            <Typography sx={{ fontSize: '0.9rem' }}>{weather.precipitation}mm</Typography>
          </>
        )}
        {warning && (
          <Tooltip title={warning}>
            <Warning sx={{ fontSize: 14, color: '#ed6c02', ml: 0.25 }} />
          </Tooltip>
        )}
      </Box>
    );
  }

  return (
    <Paper
      onClick={() => navigate('/weather')}
      sx={{
        p: 1.5, width: 220, cursor: 'pointer',
        bgcolor: '#ffffff', border: '1px solid #e8e8e8',
        '&:hover': { borderColor: '#90caf9', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
        transition: '0.2s',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        {getIcon(weather.description)}
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
            {Math.round(weather.temperature)}°C
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
            {weather.description}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
          <WaterDrop sx={{ fontSize: 14, color: '#1976d2' }} />
          <Typography variant="caption">{weather.humidity}%</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
          <Air sx={{ fontSize: 14, color: '#757575' }} />
          <Typography variant="caption">{Math.round(weather.wind_speed * 3.6)} km/h</Typography>
        </Box>
        {weather.precipitation > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
            <Grain sx={{ fontSize: 14, color: '#2196f3' }} />
            <Typography variant="caption">{weather.precipitation} mm</Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
        <Typography variant="caption" sx={{ fontSize: '0.9rem', color: BRAND.green, fontWeight: 600 }}>
          {t('viewForecast') || 'View forecast'} →
        </Typography>
        {provider && (
          <Typography variant="caption" sx={{ fontSize: '0.9rem', color: warning ? '#ed6c02' : '#9e9e9e' }}>
            {warning && <Warning sx={{ fontSize: 10, mr: 0.3, verticalAlign: 'middle' }} />}
            {provider === 'open-meteo' || provider === 'meteoblue' ? 'Meteo' : provider === 'openweathermap' ? 'OpenWeather' : provider === 'database_cache' ? (t('cachedData') || 'Cached data') : provider === 'sample' ? (t('sampleData') || 'Sample data') : provider}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default WeatherWidget;
